import React, { useState, useEffect, useRef, useCallback } from 'react';

const AnimationPreview = ({ config }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [items] = useState([1, 2, 3, 4, 5]);
  const animationRef = useRef(null);

  const triggerAnimation = useCallback(() => {
    setIsAnimating(true);
    if (animationRef.current) {
      clearTimeout(animationRef.current);
    }
    
    if (config.repeat && config.repeatCount === 0) {
      // Infinite repeat - don't set timeout
      return;
    } else if (config.repeat && config.repeatCount > 0) {
      // Limited repeat count
      const totalDuration = (config.duration + config.delay) * config.repeatCount * 1000;
      animationRef.current = setTimeout(() => setIsAnimating(false), totalDuration);
    } else {
      // Single animation
      animationRef.current = setTimeout(() => setIsAnimating(false), (config.duration + config.delay) * 1000);
    }
  }, [config]);

  useEffect(() => {
    triggerAnimation();
    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, [config, triggerAnimation]);

  const getAnimationStyle = (index = 0) => {
    // Map SwiftUI curves to CSS timing functions
    const curveMap = {
      easeInOut: 'ease-in-out',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      linear: 'linear',
      spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)', // Approximate spring curve
    };
    
    const cssCurve = curveMap[config.curve] || 'ease-in-out';
    const shouldRepeat = config.repeat && (config.repeatCount === 0 || config.repeatCount > 1);
    const animationIteration = shouldRepeat ? (config.repeatCount === 0 ? 'infinite' : config.repeatCount) : 1;
    
    const baseStyle = {
      transition: `all ${config.duration}s ${cssCurve}`,
      transitionDelay: `${config.delay + (config.type === 'staggered' ? index * config.stagger : 0)}s`,
      animationIterationCount: shouldRepeat ? animationIteration : 1,
    };

    if (!isAnimating && config.type !== 'shimmer' && !shouldRepeat) {
      return baseStyle;
    }

    switch (config.type) {
      case 'fadeIn':
        return {
          ...baseStyle,
          opacity: isAnimating ? 0 : 1,
        };

      case 'slideIn':
        const slideDistance = 100;
        const transforms = {
          up: `translateY(${isAnimating ? slideDistance : 0}px)`,
          down: `translateY(${isAnimating ? -slideDistance : 0}px)`,
          left: `translateX(${isAnimating ? slideDistance : 0}px)`,
          right: `translateX(${isAnimating ? -slideDistance : 0}px)`,
        };
        return {
          ...baseStyle,
          transform: transforms[config.direction],
          opacity: isAnimating ? 0 : 1,
        };

      case 'bounce':
        return {
          ...baseStyle,
          transform: isAnimating ? 'scale(0.8)' : 'scale(1)',
          opacity: isAnimating ? 0 : 1,
        };

      case 'scale':
        return {
          ...baseStyle,
          transform: isAnimating ? 'scale(0)' : 'scale(1)',
          opacity: isAnimating ? 0 : 1,
        };

      case 'shimmer':
        return {
          ...baseStyle,
          background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 50%, #f0f0f0 100%)',
          backgroundSize: '200% 100%',
          animation: `shimmer ${config.duration}s infinite`,
        };

      case 'staggered':
        const staggerTransforms = {
          up: `translateY(${isAnimating ? 50 : 0}px)`,
          down: `translateY(${isAnimating ? -50 : 0}px)`,
          left: `translateX(${isAnimating ? 50 : 0}px)`,
          right: `translateX(${isAnimating ? -50 : 0}px)`,
        };
        return {
          ...baseStyle,
          transform: staggerTransforms[config.direction],
          opacity: isAnimating ? 0 : 1,
        };

      default:
        return baseStyle;
    }
  };

  const renderPreview = () => {
    if (config.applyTo === 'list' || config.type === 'staggered') {
      return (
        <div className="w-full max-w-md space-y-2">
          {items.map((item, index) => (
            <div
              key={item}
              className="bg-gray-700 rounded-lg p-4"
              style={getAnimationStyle(index)}
            >
              <div className="h-4 bg-gray-600 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-600 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      );
    }

    if (config.applyTo === 'grid') {
      return (
        <div className="grid grid-cols-2 gap-4 w-full max-w-md">
          {items.slice(0, 4).map((item, index) => (
            <div
              key={item}
              className="bg-gray-700 rounded-lg p-4 aspect-square"
              style={getAnimationStyle(index)}
            >
              <div className="h-8 bg-gray-600 rounded mb-2"></div>
              <div className="h-4 bg-gray-600 rounded"></div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div
        className="bg-gray-700 rounded-lg p-8 w-64 h-32 flex items-center justify-center"
        style={getAnimationStyle()}
      >
        <div className="text-white text-lg font-semibold">Animated View</div>
      </div>
    );
  };

  return (
    <div className="w-full h-full flex items-center justify-center bg-gray-900 p-4 relative" style={{ minWidth: 0, minHeight: 0, boxSizing: 'border-box' }}>
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          @keyframes repeatFade {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          @keyframes repeatScale {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(0.8); }
          }
        `}
      </style>
      <div className="w-full h-full flex items-center justify-center">
        {renderPreview()}
      </div>
      <button
        onClick={triggerAnimation}
        className="absolute bottom-4 right-4 px-4 py-2 bg-gradient-primary hover:opacity-90 rounded text-white text-sm shadow-lg z-10"
        style={{ 
          position: 'absolute',
          bottom: '1rem',
          right: '1rem',
        }}
      >
        Replay Animation
      </button>
    </div>
  );
};

export default AnimationPreview;

